import { useState } from 'react'
import PropTypes from 'prop-types'
import Nav from 'react-bootstrap/Nav'
import { Link, NavLink } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useUser } from 'state/Auth'

/**
 *
 * @param toggle {function} The whole menu toggler; runs once the action on this MenuItem is completed, so the menu closes
 * @param to {string|function:(Promise|*)} Either a link to go to, or a function to run. If that function returns a promise, it will be waited with a spinner in place of the icon.
 * @param icon {string|string[]}
 * @param title {string} The main text - also used as the link's attr
 * @param flip {?string} If the icon should be flipped
 * @param iconColor {null|'success'|'warning'|'danger'} Change the icon color to one of those states
 * @param children Any extra content besides the title
 * @param {?PRIVILEGES_LIST} priv A privilege that must be checked to display this entry; any of the values at {@link PRIVILEGES()}
 * @param show {?boolean} If some rule decides this item is hidden from the user. If you need to check privileges, use `priv` instead.
 * @param noNav {boolean} Will create a simple Link component that won't get an "active" state
 * @param props
 * @return {JSX.Element}
 * @todo move here the badges as well?
 * @constructor
 */
const MenuItem = function ({ toggle, to, icon, title, flip = null, iconColor = null, children = null, show = undefined, priv = null, noNav = false, ...props }) {
	let [waiting, setWaiting] = useState(false)

	//split from `show` so we can make sure hooks always execute first, so the hook count is kept and React won't bitch
	//The eslint rule got disabled because the hook will only be skipped in another instance, never on the same object
	//TODO would it be more efficient if the user was part of a context? Or would it run useUser() for each entry anyway?
	if (priv && !useUser().usePrivilege(priv)) { // eslint-disable-line react-hooks/rules-of-hooks
		return null
	}

	if (show !== undefined && !show) {
		return null
	}

	let doClick, link
	if (typeof to == 'function') {
		link = '#'
		noNav = true //TODO probably should stop using Link for nothing else but an onClick
		doClick = () => {
			const result = to()
			if (result instanceof Promise) {
				setWaiting(true)
				result.then(() => {
					setWaiting(false)
					toggle()
				})
			} else {
				toggle()
			}
		}
	} else {
		link = to
		doClick = toggle
	}

	//NavLink[title] seems redundant, but it's easier to spot nav changes in some scenarios inside Sentry
	return (
		<Nav.Item as="li">
			<Nav.Link as={noNav? Link : NavLink} to={link} onClick={doClick} title={title} {...props}>
				{waiting? //FIXME Menu seems to be re-rendering all over again, which breaks this. Sorry, I give up
					<FontAwesomeIcon fixedWidth icon="spinner" pulse/>
					:
					icon && <FontAwesomeIcon flip={flip} fixedWidth icon={icon} className={iconColor? `text-${iconColor}` : ''}/>}
				{title} {children}
			</Nav.Link>
		</Nav.Item>
	)
}

/**
 * Wraps the MenuItems with the Menu toggle, which allows the menu to auto-hide when an option is clicked.
 * @param toggle
 * @returns {function(*): JSX.Element}
 */
MenuItem.withToggle = toggle => {
	return function TogglableMenuItem(props) { return MenuItem({ toggle, ...props }) }
}

MenuItem.propTypes = {
	toggle   : PropTypes.func.isRequired,
	to       : PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
	icon     : PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	title    : PropTypes.string.isRequired,
	flip     : PropTypes.oneOf(['horizontal', 'vertical', 'both']),
	iconColor: PropTypes.oneOf(['success', 'warning', 'danger']),
	children : PropTypes.node,
	priv     : PropTypes.string,
	show     : PropTypes.bool,
	noNav    : PropTypes.bool,
}

export default MenuItem
